import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, HostListener, inject, signal, Signal, ViewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, debounceTime, distinctUntilChanged, finalize, map, Observable, shareReplay, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { StopPropagation } from '../../directives/stop-propagation.directive';
import { AppState } from '../../models/state/app-state';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { InstallationsApiService } from '../../services/api-services/installations/installations.api-service';
import { IInstallationMenuItem, IInstallationParentFlat } from '../../services/api-services/installations/response/installation-menu-item.response';
import { InstallationSelectors } from '../../state/installations-state/installation-state.selectors';
import { GeneralStateAction } from '../../state/pageState/page-state.actions';
import { selectShowSiteSearch } from '../../state/pageState/page-state.selectors';
import { InstallationMenuItemNew } from '../sidebar/sidebar-installations-menu/models/installation-menu-item.model';
import { InstallationActions } from '../../state/installations-state/installation-state.actions';
import { RouterLinkSubDomainDirective } from '../../directives/router-link-sub-domain.directive';

@Component({
  selector: 'app-site-search',
  imports: [
    CommonModule,
    StopPropagation,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    HighlightPipe,
    RouterModule,
    TranslatePipe,
    RouterLinkSubDomainDirective
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './site-search.component.html'
})
export class SiteSearchComponent {
  @ViewChild("siteSearchInput") inputField: ElementRef<HTMLInputElement>;


  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Show this window
    if (event.ctrlKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openWindow();

    }// Don't do any internal actions, if the window is not open
    if (!this.showSiteSearch()) return;

    if (this.fullResult().length > 0) {
      if (event.key === 'ArrowDown') {
        this.selectedIndex.set(Math.min(this.selectedIndex() + 1, this.fullResult().length - 1));
      }

      else if (event.key === 'ArrowUp') {
        this.selectedIndex.set(Math.max(this.selectedIndex() - 1, 0));

      }
      else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.fullResult()[this.selectedIndex()]) {
          this.openInstallation(this.fullResult()[this.selectedIndex()]);
        }
      }
    }
    // close window
    if (event.key === 'Escape') {
      event.preventDefault();
      this.clearOrCloseWindow();
    }
  }
  store: Store<AppState> = inject(Store);
  installationService: InstallationsApiService = inject(InstallationsApiService);
  router: Router = inject(Router);
  showSiteSearch: Signal<boolean | undefined> = toSignal(this.store.select(selectShowSiteSearch));
  form = new FormGroup({ search: new FormControl<string>("") });
  get controls() { return this.form.controls };


  selectedIndex: WritableSignal<number> = signal(0);
  latestInstallations$: Observable<InstallationMenuItemNew[] | undefined> =
    this.store.select(InstallationSelectors.getLatestInstallations);
  searchText$ = this.controls.search.valueChanges.pipe(
    startWith(''),
    map(v => v?.trim() || null),
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this.isLoading.set(true)),
    shareReplay(1)
  );
  filteredLatestInstallations$: Observable<InstallationMenuItemNew[]> =
    combineLatest([
      this.searchText$,
      this.latestInstallations$.pipe(startWith([]))
    ]).pipe(map(([a, latest]) => {
      if (!latest) return [];

      return a ? latest.filter((inst) =>
        ((inst.refCode ?? "").toLowerCase()
          + (inst.address ?? "").toLowerCase()
          + (inst.name ?? "").toLowerCase()
          + (inst.alias ?? "").toLowerCase()
        ).includes(a)) : latest;
    }),
      map(list => this.dedupeByRefCode(list ?? []).slice(0, 10))); // Top 10, unique by refCode

  isLoading: WritableSignal<boolean | undefined> = signal(undefined);
  filteredInstallations$: Observable<InstallationMenuItemNew[]> =
    this.searchText$.pipe(
      switchMap(searchText => {
        this.isLoading.set(true);

        return this.installationService.searchInstallations(searchText, 10).pipe(
          withLatestFrom(this.filteredLatestInstallations$.pipe(startWith([]))),
          map(([results, latest]) => {
            if (!results) return [];
            const latestIds = new Set(latest?.map(x => x.refCode) ?? []);
            return this.dedupeByRefCode(results.filter(item => !latestIds.has(item.refCode)))
              .map(item => {
                item.parentsFlat = this.flatParrents(item.parent).reverse();
                return item;
              });
          }),
          finalize(() => {
            this.isLoading.set(false);
          })
        );
      }),
      tap(() => this.selectedIndex.set(0))
    );
  fullResult: Signal<InstallationMenuItemNew[]> = toSignal(
    combineLatest([
      this.filteredLatestInstallations$.pipe(
        startWith([] as InstallationMenuItemNew[])
      ),
      this.filteredInstallations$.pipe(startWith([] as InstallationMenuItemNew[]))
    ]).pipe(
      tap(() => this.selectedIndex.set(0)),
      map(([latest, filtered]) => {
        return latest.concat(filtered);
      }),
    ),
    { initialValue: [] as InstallationMenuItemNew[] }
  );

  // Guard against duplicate refCodes in a result list — Angular @for tracks installations by
  // refCode, and a repeated one (e.g. the same recent visited twice) throws NG0955.
  private dedupeByRefCode(items: InstallationMenuItemNew[]): InstallationMenuItemNew[] {
    const seen = new Set<string>();
    return items.filter(item => {
      const key = item.refCode ?? '';
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  flatParrents(item: IInstallationParentFlat | undefined): IInstallationParentFlat[] {
    if (!item)
      return [];
    const result: IInstallationParentFlat[] = [];
    let current: IInstallationParentFlat | undefined = item;

    while (current) {
      result.push(current); // push the reference directly
      current = current.parent;
    }

    return result;
  }


  openInstallation(item: IInstallationMenuItem) {
    this.store.dispatch(GeneralStateAction.setShowInstallationMenu({ showInstallationMenu: false }));
    this.router.navigate([item.refCode, 'dashboard']); // Navigate to installation dashboard
    this.closeWindow();
  }


  constructor() {

    this.store.dispatch(InstallationActions.getLatestInstallations());

    effect(() => {
      if (this.showSiteSearch()) {
        setTimeout(() => {
          this.inputField?.nativeElement?.focus();
        }, 0);
      }
    })
  }
  closeWindow() {
    this.controls.search.setValue(null);
    this.store.dispatch(GeneralStateAction.setShowSiteSearchState({ showSiteSearch: false }));
  }

  clearOrCloseWindow() {
    if (!this.controls.search.value)
      this.store.dispatch(GeneralStateAction.setShowSiteSearchState({ showSiteSearch: false }));

    this.controls.search.setValue(null);
  }

  openWindow() {
    this.store.dispatch(GeneralStateAction.setShowSiteSearchState({ showSiteSearch: true }));
  }

  iconName(type: string) {
    type = type.trim().toLowerCase();
    switch (type) {
      case 'installation':
        return 'roofing'
      case 'subinstallation':
        return 'apartment'
      case 'topgroup':
        return 'grain'
      case 'group':
        return 'account_tree'
      default:
        return 'roofing';
    }
  }

  iconClassName(type: string) {
    switch (type) {
      case 'installation':
        return 'text-insta-installation'
      case 'subInstallation':
        return 'text-insta-sub-installation'
      case 'topGroup':
        return 'text-insta-top-group'
      case 'group':
        return 'text-insta-group'
      default:
        return 'text-insta-intallation';
    }
  }


}
